import { getCurrentUserId } from 'src/utils/user-context';

type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';

export class Logger {
  private getContext() {
    return {
      userId: getCurrentUserId(),
    };
  }
  private log(level: LogLevel, message: string, additionalContext?: Record<string, any>) {
    const context = {
      ...additionalContext,
      ...this.getContext(),
    };
    // Report to New Relic
    if (window.newrelic) {
      window.newrelic.log(message, {
        level,
        customAttributes: context,
      });
    } else {
      console.log('New Relic not available to log message');
    }

    // Always log to console in development or if explicitly needed
    if (window.location.hostname === 'localhost') {
      const consoleMethod =
        level === 'ERROR'
          ? console.error
          : level === 'WARN'
            ? console.warn
            : level === 'DEBUG'
              ? console.debug
              : console.info;
      consoleMethod(`[Development] ${message}`, context);
    }
  }

  info(message: string, attributes?: Record<string, any>) {
    this.log('INFO', message, attributes);
  }
  warn(message: string, attributes?: Record<string, any>) {
    this.log('WARN', message, attributes);
  }

  /**
   * Logs an error to New Relic, and if the noticeError option is set to true, it will also send a notice to New Relic with the error message and stack trace.
   * @param {string} message - The error message to log
   * @param {Record<string, any>} [additionalContext] - Additional context to log with the error
   * @param {boolean} reportError - Whether to send a notice to New Relic (this triggers newrelic to save session for the error). Tipically set to true for unexpected errors
   */
  error(message: string, additionalContext?: Record<string, any>, reportError: boolean = false) {
    const errorObj = new Error(message);
    const context = {
      ...additionalContext,
      stackTrace: errorObj.stack,
    };

    this.log('ERROR', message, context);

    // Report to New Relic
    if (window.newrelic && reportError) {
      window.newrelic.noticeError(errorObj, context);
    }
  }
}
// Export singleton instance
export const logger = new Logger();
